import { Stack } from "@mui/system";
import { Typography } from "@mui/material";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";
import { BRAND_STATUS_LABELS } from "./utils/helpersOfBrands";

type BrandStatus =
  | "ready"
  | "not_ready"
  | "config_issue"
  | "account_issue"
  | "not_connected";

const STATUS_CONFIG: Record<
  BrandStatus,
  { color: string; bg: string; border: string; icon: JSX.Element }
> = {
  ready: {
    color: "#1E4620",
    bg: "#EDF7ED",
    border: "#2E7D3280",
    icon: <CheckCircleOutlinedIcon fontSize="small" />,
  },
  not_ready: {
    color: "#5F2120",
    bg: "#FDEDED",
    border: "#D32F2F80",
    icon: <ErrorOutlineIcon fontSize="small" />,
  },
  config_issue: {
    color: "#663C00",
    bg: "#FFF4E5",
    border: "#ED6C0280",
    icon: <WarningAmberIcon fontSize="small" />,
  },
  account_issue: {
    color: "#663C00",
    bg: "#FFF4E5",
    border: "#ED6C0280",
    icon: <WarningAmberIcon fontSize="small" />,
  },
  not_connected: {
    color: "#00000099",
    bg: "#0000001F",
    border: "#9E9E9E",
    icon: <DoNotDisturbIcon fontSize="small" />,
  },
};

interface Props {
  status?: string | null;
  brandIsActive?: number | null;
  accountStatusName?: string | null;
}

// Status badge for the Brands module (ready/not_ready/config_issue/
// account_issue/not_connected), plus the sub-label shown underneath it.
// Kept local to this module rather than extending the shared
// utilitiesOfBrandConfig/StatusChip.tsx, which is a different status
// vocabulary used by the (untouched) legacy module.
const BrandsStatusBadge = ({
  status,
  brandIsActive,
  accountStatusName,
}: Props) => {
  const key = status?.toLowerCase() as BrandStatus | undefined;
  const config = key ? STATUS_CONFIG[key] : undefined;

  // Sub-label rules, per spec:
  //  - Account Issue rows show the query's account_status_name, which says
  //    *why* the account is problematic. Confirmed to apply for both
  //    brand_is_active = 0 and = 1, so it is not gated on that flag.
  //  - Every other status keeps the generic "Inactive Brand" wording, still
  //    shown only when brand_is_active = 0.
  const isAccountIssueLabel = key === "account_issue" && !!accountStatusName;
  const subLabel = isAccountIssueLabel ? accountStatusName : "Inactive Brand";

  return (
    <Stack direction="column" spacing={0.5} alignItems="flex-start">
      {config ? (
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{
            px: 0.5,
            py: 0.3,
            borderRadius: "4px",
            backgroundColor: config.bg,
            border: `1px solid ${config.border}`,
            width: "fit-content",
          }}
        >
          <span style={{ display: "flex", color: config.color }}>
            {config.icon}
          </span>
          <span
            style={{ fontSize: "13px", fontWeight: 500, color: config.color }}
          >
            {BRAND_STATUS_LABELS[key as string] ?? status}
          </span>
        </Stack>
      ) : (
        "N/A"
      )}
      {(brandIsActive === 0 || isAccountIssueLabel) && (
        <Typography
          variant="caption"
          sx={{ color: "text.disabled", fontSize: "11px" }}
        >
          {subLabel}
        </Typography>
      )}
    </Stack>
  );
};

export default BrandsStatusBadge;
